import React from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import { TransferItem } from './InventoryTransfer.hooks';
import { TableStatusChip } from '../../common/Table/TableStatusChip';
import { ViewButton } from '../../common/Table/Table.styles';
import { mapWarehouseStatus } from '../../common/status';

export interface ProductTypeGroupProps {
  productType: string;
  products: TransferItem[];
  isExpanded: boolean;
  onToggle: () => void;
  showCheckboxes?: boolean;
  selectedProductIds?: Set<string>;
  onProductToggle?: (id: string) => void;
  confirmationType?: 'accept' | 'reject' | 'view';
  /** Ver detalle de pieza (mismo modal que gestión en almacén) */
  onViewPieceDetail?: (warehouseInventoryProductId: number) => void;
}

const ProductTypeGroup: React.FC<ProductTypeGroupProps> = ({
  productType,
  products,
  isExpanded,
  onToggle,
  showCheckboxes = false,
  selectedProductIds = new Set(),
  onProductToggle,
  confirmationType = 'view',
  onViewPieceDetail,
}) => {
  const selectedCount = showCheckboxes
    ? products.filter((p) => selectedProductIds.has(p.id)).length
    : 0;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1.5,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header del Grupo */}
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          bgcolor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.common.white, 0.06)
              : theme.palette.grey[50],
          borderBottom: isExpanded ? '1px solid' : 'none',
          borderColor: 'divider',
          cursor: 'pointer',
          '&:hover': {
            bgcolor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.1)
                : theme.palette.grey[100],
          },
          transition: 'background-color 0.2s',
        })}
        onClick={onToggle}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              transition: 'transform 0.2s',
              transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            }}
          >
            {isExpanded ? (
              <ExpandLessIcon sx={{ fontSize: '1.25rem', color: 'text.secondary' }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: '1.25rem', color: 'text.secondary' }} />
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 0.5,
              }}
            >
              {productType}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {products.length} {products.length === 1 ? 'pieza' : 'piezas'}
              </Typography>
              {showCheckboxes && selectedCount > 0 && (
                <Chip
                  label={`${selectedCount}/${products.length} verificados`}
                  size="small"
                  sx={(theme) => ({
                    height: '20px',
                    fontSize: '0.7rem',
                    bgcolor: alpha(
                      theme.palette.success.main,
                      theme.palette.mode === 'dark' ? 0.22 : 0.12,
                    ),
                    color:
                      theme.palette.mode === 'dark'
                        ? theme.palette.success.light
                        : theme.palette.success.dark,
                    fontWeight: 600,
                  })}
                />
              )}
            </Box>
          </Box>
        </Box>
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          sx={{
            minWidth: 'auto',
            px: 1.5,
            py: 0.5,
            color: 'text.secondary',
            textTransform: 'none',
            fontSize: '0.75rem',
            '&:hover': {
              bgcolor: 'transparent',
              color: 'text.primary',
            },
          }}
        >
          {isExpanded ? 'Ocultar' : 'Ver'} detalles
        </Button>
      </Box>

      {/* Contenido Expandible */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ bgcolor: 'background.paper' }}>
          <TableContainer
            component={Box}
            sx={{
              border: 'none',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={(theme) => ({
                    bgcolor:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.06)
                        : theme.palette.grey[50],
                  })}
                >
                  {showCheckboxes && confirmationType === 'accept' && (
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        width: 60,
                      }}
                    >
                      ✓
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                    }}
                  >
                    Código
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                    }}
                  >
                    Nombre
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                    }}
                  >
                    Estado
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                    }}
                  >
                    Ubicación
                  </TableCell>
                  {onViewPieceDetail && (
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        width: 56,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Ver
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((item, index) => {
                  const isSelected = selectedProductIds.has(item.id);
                  return (
                    <TableRow
                      key={item.id || index}
                      sx={(theme) => ({
                        '&:hover': {
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? alpha(theme.palette.common.white, 0.04)
                              : theme.palette.grey[50],
                        },
                        transition: 'background-color 0.2s',
                        bgcolor:
                          showCheckboxes && isSelected
                            ? alpha(
                                theme.palette.success.main,
                                theme.palette.mode === 'dark' ? 0.18 : 0.1,
                              )
                            : 'transparent',
                        borderLeft: showCheckboxes && isSelected ? '3px solid' : 'none',
                        borderLeftColor:
                          showCheckboxes && isSelected ? 'success.main' : 'transparent',
                      })}
                    >
                      {showCheckboxes && confirmationType === 'accept' && (
                        <TableCell>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => onProductToggle?.(item.id)}
                            color="primary"
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                      )}
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          color: 'text.primary',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.productCode}
                      </TableCell>
                      <TableCell sx={{ color: 'text.primary', whiteSpace: 'nowrap' }}>
                        {item.productName}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {(() => {
                          const chip = mapWarehouseStatus(item.status);
                          return (
                            <TableStatusChip label={chip.label} color={chip.color} size="small" />
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {item.location}
                      </TableCell>
                      {onViewPieceDetail && (
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          {item.warehouseInventoryProductId != null &&
                          item.warehouseInventoryProductId > 0 ? (
                            <Tooltip title="Ver detalle de la pieza">
                              <ViewButton
                                size="small"
                                aria-label="Ver detalle de la pieza"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onViewPieceDetail(item.warehouseInventoryProductId!);
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </ViewButton>
                            </Tooltip>
                          ) : null}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ProductTypeGroup;
